import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartCard,
  FONT,
  StatTile,
  VIZ,
  VizTooltip,
  axisProps,
  compact,
  gridProps,
} from "../../Common/Viz";

// The API hands back pre-formatted strings like "25,887,372.00". Trailing
// decimals are noise at these magnitudes, so they are dropped.
const taka = (value) =>
  value === undefined || value === null
    ? "৳0"
    : "৳" + String(value).replace(/\.00$/, "");

const asMoney = (value) => "৳" + Number(value || 0).toLocaleString("en-US");

// The API now returns one bucket per month of the selected range, already
// labelled ("Jan 2026" / "January 2026"). The fallback covers the older payload
// that sent a bare lowercase month name.
const titleCase = (value) =>
  String(value || "").charAt(0).toUpperCase() + String(value || "").slice(1);

const formatMonthlyReport = (report, key) => {
  if (!report) return [];

  return report.map((item) => ({
    name: item.name || titleCase(item.month).slice(0, 3),
    month: item.month ? titleCase(item.month) : "",
    [key]: item[key],
  }));
};

/**
 * A bank account. The balance sign is carried by an icon and a word, never by
 * colour alone, and the track is a lighter step of the fill's own ramp.
 */
export const BankBalanceCard = ({ bankData }) => {
  const raw = Number(bankData.remaining_balance_raw || 0);
  // Three states, not two - a zero balance is not "in credit".
  const state =
    raw > 0
      ? { label: "In credit", color: VIZ.status.good, icon: "check-circle" }
      : raw === 0
      ? { label: "No balance", color: VIZ.inkMuted, icon: "minus-circle" }
      : { label: "Overdrawn", color: VIZ.status.critical, icon: "exclamation-circle" };

  return (
    <div
      style={{
        background: VIZ.surface,
        border: `1px solid ${VIZ.border}`,
        borderRadius: "10px",
        padding: "18px",
        height: "100%",
        fontFamily: FONT,
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 style={{ color: VIZ.ink, fontSize: "14px", fontWeight: 600, margin: 0 }}>
            {bankData.bank_name}
          </h6>
          <p style={{ color: VIZ.inkMuted, fontSize: "11px", margin: "2px 0 0" }}>
            {bankData.branch} ·{" "}
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {bankData.account_number}
            </span>
          </p>
        </div>
        <span
          className="d-inline-flex align-items-center"
          style={{
            gap: "4px",
            color: state.color,
            fontSize: "11px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          <i className={`fa fa-${state.icon}`} aria-hidden="true" />
          {state.label}
        </span>
      </div>

      <div style={{ marginTop: "14px" }}>
        <p style={{ color: VIZ.inkSecondary, fontSize: "11px", margin: 0 }}>
          Available balance
        </p>
        <div
          style={{
            color: VIZ.ink,
            fontSize: "24px",
            fontWeight: 600,
            lineHeight: 1.15,
          }}
        >
          {taka(bankData.remaining_balance)}
        </div>
      </div>

      {/* Three equal columns so long figures wrap inside the card, never past it. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "10px",
          marginTop: "12px",
          paddingTop: "12px",
          borderTop: `1px solid ${VIZ.border}`,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {[
          ["Opening", bankData.initial_balance],
          ["Deposits", bankData.total_deposit],
          ["Expenses", bankData.total_expenses],
        ].map(([label, amount]) => (
          <div key={label} style={{ minWidth: 0 }}>
            <p style={{ color: VIZ.inkMuted, fontSize: "11px", margin: 0 }}>{label}</p>
            <strong
              style={{
                color: VIZ.inkSecondary,
                fontSize: "12px",
                overflowWrap: "anywhere",
              }}
            >
              {taka(amount)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const MonthlyBars = ({ data, dataKey, color }) => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid {...gridProps} />
      <XAxis dataKey="name" {...axisProps} />
      <YAxis {...axisProps} width={54} tickFormatter={compact} />
      <Tooltip
        cursor={{ fill: "rgba(11,11,11,0.04)" }}
        content={<VizTooltip format={asMoney} />}
      />
      <Bar
        dataKey={dataKey}
        name={dataKey === "expense" ? "Expense" : "Deposit"}
        fill={color}
        maxBarSize={24}
        radius={[4, 4, 0, 0]}
      />
    </BarChart>
  </ResponsiveContainer>
);

/**
 * The accounts dashboard body - totals, bank balances and the monthly
 * expense/deposit charts. Rendered by both the accounts dashboard and the admin
 * dashboard, so it stays presentational and takes the `/accounts/dashboard`
 * payload as a prop.
 */
const AccountsDashboardContent = ({ data, stale = false }) => {
  const accounts = data || {};
  const monthlyExpenses = formatMonthlyReport(
    accounts.monthly_expenses?.monthly_report,
    "expense"
  );
  const monthlyDeposits = formatMonthlyReport(
    accounts.monthly_deposits?.monthly_report,
    "deposit"
  );
  const banks = accounts.bank_balances || [];
  // Fall back to the cumulative figure when an older payload has no range
  // totals, so the tiles never blank out mid-deploy.
  const inRange = (rangeValue, cumulative) =>
    rangeValue === undefined || rangeValue === null ? cumulative : rangeValue;
  const rangeNote = accounts.range?.label || "selected range";

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Totals */}
      <div className="row row-sm" style={{ opacity: stale ? 0.45 : 1 }}>
        <div className="col-md-4">
          <StatTile
            label="Expenses"
            value={taka(inRange(accounts.range_expenses, accounts.total_expenses))}
            sub={`${rangeNote} · ${taka(accounts.total_expenses)} all time`}
            accent={VIZ.series[1]}
          />
        </div>
        <div className="col-md-4 mg-t-15 mg-md-t-0">
          <StatTile
            label="Deposits"
            value={taka(inRange(accounts.range_deposit, accounts.total_deposit))}
            sub={`${rangeNote} · ${taka(accounts.total_deposit)} all time`}
            accent={VIZ.series[2]}
          />
        </div>
        <div className="col-md-4 mg-t-15 mg-md-t-0">
          <StatTile
            label="Vendor payments"
            value={taka(
              inRange(accounts.range_vendor_payment, accounts.total_vendor_payment)
            )}
            sub={`${rangeNote} · ${taka(accounts.total_vendor_payment)} all time`}
            accent={VIZ.series[0]}
          />
        </div>
      </div>

      {/* Bank balances */}
      <h6
        style={{
          color: VIZ.inkSecondary,
          fontSize: "13px",
          fontWeight: 600,
          margin: "22px 0 12px",
        }}
      >
        Bank accounts{" "}
        <span style={{ color: VIZ.inkMuted, fontWeight: 400 }}>
          ({banks.length})
        </span>
      </h6>
      <div className="row row-sm" style={{ opacity: stale ? 0.45 : 1 }}>
        {banks.length ? (
          banks.map((bank) => (
            <div key={bank.bank_id} className="col-lg-4 col-md-6 mg-b-15">
              <BankBalanceCard bankData={bank} />
            </div>
          ))
        ) : (
          <div className="col-12">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                background: VIZ.surface,
                border: `1px solid ${VIZ.border}`,
                borderRadius: "10px",
                minHeight: "120px",
                color: VIZ.inkMuted,
                fontSize: "13px",
              }}
            >
              No bank accounts to show.
            </div>
          </div>
        )}
      </div>

      {/* Monthly charts */}
      <div className="row row-sm mg-t-10">
        <div className="col-lg-6">
          <ChartCard
            title="Expenses by month"
            note="Bucketed by calendar month across the selected range."
            stale={stale}
            rows={monthlyExpenses}
            columns={[
              { key: "month", label: "Month" },
              { key: "expense", label: "Expense", align: "right", format: asMoney },
            ]}
          >
            <MonthlyBars data={monthlyExpenses} dataKey="expense" color={VIZ.series[1]} />
          </ChartCard>
        </div>

        <div className="col-lg-6 mg-t-15 mg-lg-t-0">
          <ChartCard
            title="Deposits by month"
            note="Bucketed by calendar month across the selected range."
            stale={stale}
            rows={monthlyDeposits}
            columns={[
              { key: "month", label: "Month" },
              { key: "deposit", label: "Deposit", align: "right", format: asMoney },
            ]}
          >
            <MonthlyBars data={monthlyDeposits} dataKey="deposit" color={VIZ.series[2]} />
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

export default AccountsDashboardContent;
